import React, { useMemo } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { useTranslation } from "react-i18next";

import { ThemedText } from "@/components/ThemedText";
import Dropdown, { DropdownOption } from "@/components/ui/Dropdown";
import { NOTIFICATION_TYPES } from "@/constants/notification-types";
import { useThemeColor } from "@/hooks/useThemeColor";
import type {
  PostsSearchFilters,
  SearchDateField,
} from "@/utils/omniSearch.web";

type Props = {
  draft: PostsSearchFilters;
  onChange: (next: PostsSearchFilters) => void;
  onApply: () => void;
  onReset: () => void;
};

const toDateInputValue = (date: Date | null | undefined) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseDateInputValue = (value: string): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date();
  date.setFullYear(year);
  date.setMonth(month - 1);
  date.setDate(day);
  date.setHours(0, 0, 0, 0);
  return date;
};

export function SearchFiltersForm({
  draft,
  onChange,
  onApply,
  onReset,
}: Props) {
  const { t } = useTranslation();
  const color = useThemeColor({}, "text");
  const backgroundColor = useThemeColor({}, "background");
  const tintColor = useThemeColor({}, "tint");
  const stripesColor = useThemeColor({}, "stripes");

  const setField = <K extends keyof PostsSearchFilters>(
    key: K,
    value: PostsSearchFilters[K],
  ) => {
    onChange({ ...draft, [key]: value });
  };

  const setDateField = (field: SearchDateField) => {
    setField("dateField", field);
  };

  const eventTypeOptions = useMemo<DropdownOption[]>(
    () => [
      { value: "", label: t("ANY") },
      ...Object.entries(NOTIFICATION_TYPES).map(([value, type]) => ({
        value,
        label: t(type.title),
        icon: type.icon,
      })),
    ],
    [t],
  );

  return (
    <View style={styles.container}>
      <View style={styles.field}>
        <ThemedText style={styles.label}>{t("FILTER_EVENT_TYPE")}</ThemedText>
        <Dropdown
          testID="filter-event-type"
          value={draft.eventType ?? ""}
          onChange={(value) => setField("eventType", value || undefined)}
          options={eventTypeOptions}
          placeholder={t("ANY")}
          className="w-full"
          triggerClassName="w-full justify-between"
        />
      </View>

      <View style={[styles.dateCard, { borderColor: stripesColor }]}>
        <ThemedText style={styles.label}>{t("FILTER_DATE_RANGE")}</ThemedText>

        <View style={styles.dateToggleRow}>
          <TouchableOpacity
            onPress={() => setDateField("createdAt")}
            style={[
              styles.dateToggle,
              { borderColor: stripesColor },
              draft.dateField === "createdAt" && {
                backgroundColor: tintColor,
                borderColor: tintColor,
              },
            ]}
          >
            <ThemedText
              style={{
                color: draft.dateField === "createdAt" ? "#fff" : color,
                fontSize: 13,
                fontWeight: "600",
              }}
            >
              {t("FILTER_DATE_FIELD_CREATED")}
            </ThemedText>
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => setDateField("eventDate")}
            style={[
              styles.dateToggle,
              { borderColor: stripesColor },
              draft.dateField === "eventDate" && {
                backgroundColor: tintColor,
                borderColor: tintColor,
              },
            ]}
          >
            <ThemedText
              style={{
                color: draft.dateField === "eventDate" ? "#fff" : color,
                fontSize: 13,
                fontWeight: "600",
              }}
            >
              {t("FILTER_DATE_FIELD_EVENT")}
            </ThemedText>
          </TouchableOpacity>
        </View>

        <View style={styles.dateRow}>
          <View style={styles.dateCol}>
            <ThemedText style={styles.subLabel}>
              {t("FILTER_DATE_FROM")}
            </ThemedText>
            <input
              data-testid="filter-date-from"
              type="date"
              value={toDateInputValue(draft.dateFrom)}
              onChange={(e) =>
                setField("dateFrom", parseDateInputValue(e.target.value))
              }
              style={{
                color,
                backgroundColor,
                border: `1px solid ${stripesColor}`,
                padding: 10,
                borderRadius: 12,
                fontSize: 14,
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </View>
          <View style={styles.dateCol}>
            <ThemedText style={styles.subLabel}>
              {t("FILTER_DATE_TO")}
            </ThemedText>
            <input
              data-testid="filter-date-to"
              type="date"
              value={toDateInputValue(draft.dateTo)}
              onChange={(e) =>
                setField("dateTo", parseDateInputValue(e.target.value))
              }
              style={{
                color,
                backgroundColor,
                border: `1px solid ${stripesColor}`,
                padding: 10,
                borderRadius: 12,
                fontSize: 14,
                width: "100%",
                boxSizing: "border-box",
              }}
            />
          </View>
        </View>
      </View>

      <View style={styles.actions}>
        <TouchableOpacity
          testID="filter-reset-button"
          onPress={onReset}
          style={[styles.actionBtn, styles.resetBtn, { borderColor: stripesColor }]}
        >
          <ThemedText style={{ color, fontWeight: "600" }}>
            {t("RESET")}
          </ThemedText>
        </TouchableOpacity>
        <TouchableOpacity
          testID="filter-apply-button"
          onPress={onApply}
          style={[styles.actionBtn, { backgroundColor: tintColor }]}
        >
          <ThemedText style={{ color: "#fff", fontWeight: "700" }}>
            {t("APPLY")}
          </ThemedText>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: "100%",
    gap: 14,
  },
  field: {
    width: "100%",
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: "600",
  },
  subLabel: {
    fontSize: 12,
    opacity: 0.7,
  },
  dateCard: {
    padding: 12,
    borderRadius: 12,
    borderWidth: 1,
    gap: 10,
  },
  dateToggleRow: {
    flexDirection: "row",
    gap: 6,
  },
  dateToggle: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: 10,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  dateRow: {
    flexDirection: "row",
    gap: 8,
  },
  dateCol: {
    flex: 1,
    gap: 4,
  },
  actions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 6,
  },
  actionBtn: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  resetBtn: {
    borderWidth: 1,
    backgroundColor: "transparent",
  },
});

export default SearchFiltersForm;
